import { HubConnection } from '@microsoft/signalr';
import { IAppContext } from 'App/AppContext';
import {
  CopilotConversation,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import User from 'models/user';
import { Dispatch, SetStateAction, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CopilotAgentEvent,
  CopilotAgentEventKind,
  CopilotConversationContext,
  CopilotHubMessageType,
  CopilotStatus,
  copilotStatusMessages,
  startCopilotChatHubConnection,
} from 'services/SignalR/CopilotHub';
import logger from 'services/Logging/logService';
import { IHubConnection } from 'services/SignalR/GeneralHub';
import AppError from 'utils/appError';
import { CopilotStreamAction } from './copilotStreamReducer';

interface IUseCopilotStreamingProps {
  appContext: IAppContext;
  conversation: CopilotConversation | undefined;
  setConversation: Dispatch<SetStateAction<CopilotConversation | undefined>>;
  setConversationCreator: Dispatch<SetStateAction<User | undefined>>;
  setConversationDate: Dispatch<SetStateAction<Date | undefined>>;
  setPreviewContent: Dispatch<SetStateAction<CopilotPreviewContent>>;
  setStreamStatus: Dispatch<SetStateAction<string | undefined>>;
  dispatchStream: Dispatch<CopilotStreamAction>;
  // every agent event of the current run, after the stream got it (a process hand-off reads the tool results)
  onAgentEventObserved?: (agentEvent: CopilotAgentEvent) => void;
}

export const useCopilotStreaming = ({
  appContext,
  conversation,
  setConversation,
  setConversationCreator,
  setConversationDate,
  setPreviewContent,
  setStreamStatus,
  dispatchStream,
  onAgentEventObserved,
}: IUseCopilotStreamingProps) => {
  const { t } = useTranslation(['copilot']);

  const addMessage = useCallback(
    (message: string) => {
      const newConversation = conversation?.clone() || new CopilotConversation();
      const newMessage = new CopilotConversationItem();
      newMessage.response = message;
      newMessage.type = CopilotConversionItemType.Copilot;
      newConversation.thread.push(newMessage);
      setConversation(newConversation);
    },
    [conversation, setConversation],
  );

  const setStatus = useCallback(
    (status: CopilotStatus) => {
      const statusMsg = copilotStatusMessages(t)[status];
      setStreamStatus(statusMsg);
    },
    [setStreamStatus, t],
  );

  const setConversationContext = useCallback(
    (context: string, type: CopilotConversationContext) => {
      switch (type) {
        case CopilotConversationContext.UserId:
          setConversationCreator(appContext.globalDataCache.users.get(context));
          break;
        case CopilotConversationContext.Date:
          setConversationDate(new Date(context));
          break;
      }
    },
    [appContext.globalDataCache.users, setConversationCreator, setConversationDate],
  );

  const onAgentEvent = useCallback(
    (agentEvent: CopilotAgentEvent) => {
      if (agentEvent.kind === CopilotAgentEventKind.TextDelta) {
        if (agentEvent.messageType === CopilotHubMessageType.Document) {
          setPreviewContent(CopilotPreviewContent.Document);
        }
      }

      dispatchStream({ type: 'event', event: agentEvent });
      onAgentEventObserved?.(agentEvent);
    },
    [dispatchStream, onAgentEventObserved, setPreviewContent],
  );

  // The connection of the current run. Every connection gets a handler of its own, which drops the agent events of a
  // connection that is no longer the current one (a stopped run that still delivers), so they never reach the stream.
  // Compared by the connection itself: its id changes on every automatic reconnect.
  const currentConnectionRef = useRef<HubConnection | undefined>(undefined);

  const getCopilotHubConnection = useCallback(async (): Promise<IHubConnection | null> => {
    try {
      let started: IHubConnection | null = null;
      const onConnectionAgentEvent = (agentEvent: CopilotAgentEvent) => {
        if (started === null || started.connection !== currentConnectionRef.current) {
          logger.debug('Copilot stream: dropped an agent event of an earlier connection', agentEvent.kind);

          return;
        }

        onAgentEvent(agentEvent);
      };

      started = await startCopilotChatHubConnection(
        appContext,
        addMessage,
        setStatus,
        setConversationContext,
        onConnectionAgentEvent,
      );
      currentConnectionRef.current = started?.connection;

      return started;
    } catch (err) {
      const appErr = AppError.fromApiError(err);
      appContext.showNotification(appErr.message, true);

      return null;
    }
  }, [addMessage, appContext, onAgentEvent, setConversationContext, setStatus]);

  return { getCopilotHubConnection };
};
